import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Divider, Radio } from 'antd'
import { useAuth } from '../../../context/auth-context'
import { MeetingHrFeedbackResult, MeetingStatus, HrManageMeetingInput, AppraisalWithFeedbackDto, MeetingPdfStatus } from '../../../types'
import { PerModal } from '../../../components/custom_components/Modal'
import { useDateTime } from '../../../shared/useDateTime'
import { appraisalTableUiProps } from './appraisalTable.uiprops'
import { getConclusionHrefByMeeting } from '../../meeting/util/hrefUtils'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerButton from '../../../components/custom_components/Button'
import PerText from '../../../components/custom_components/Text'
import PerSpacer from '../../../components/custom_components/Spacer'
import TextArea from 'antd/lib/input/TextArea'
import RadioGroup from 'antd/lib/radio/group'
import { PerCardInfo } from '../../../components/custom_components/CardInfo'

interface Props {
  open: boolean
  resolveFeedbackInProgress: boolean
  modalData: AppraisalWithFeedbackDto
  modalError?: string
  saveCommentInProgress: boolean
  hrComment?: string
  hrResult: MeetingHrFeedbackResult
  setHrComment: (text: string) => void
  setHrResult: (result: MeetingHrFeedbackResult) => void
  closeModal: () => void
  onSubmit: (formData: { employeeId: string; hrManageMeetingInput: HrManageMeetingInput }) => void
  saveComment: (formData: { employeeId: string; hrManageMeetingInput: HrManageMeetingInput }) => void
}

export const DisputedAppraisalFeedbackModal: FC<Props> = ({
  open,
  saveCommentInProgress,
  saveComment,
  resolveFeedbackInProgress,
  hrComment,
  hrResult,
  setHrComment,
  setHrResult,
  closeModal,
  onSubmit,
  modalData,
  modalError,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [isSubmitting, toggleSubmitting] = useState<boolean>(false)
  const { formatDateShort, formatDateTimeShort } = useDateTime(user)
  const { feedbackColor, feedbackHeading, feedbackResultText, feedbackResultColor } = appraisalTableUiProps(
    t,
    formatDateShort,
    formatDateTimeShort,
  )

  const hrFeedbackIsOpen = modalData.hrResult === MeetingHrFeedbackResult.NoResult
  const meetingStatus: MeetingStatus = modalData.status

  const createConfirmButtonTooltip = (): string | undefined => {
    if (hrResult !== MeetingHrFeedbackResult.NoResult && !!hrComment && hrComment.trim().length >= 2) {
      return undefined
    }
    if (!!hrComment && hrComment.trim().length > 0 && hrComment.trim().length < 2 && hrResult !== MeetingHrFeedbackResult.NoResult) {
      return t('companySettingsView.appraisalTab.table.feedback.modal.btnConfirmTooltip.commentTooShort')
    } else {
      return t('companySettingsView.appraisalTab.table.feedback.modal.btnConfirmTooltip.genericMessage')
    }
  }

  const createSaveCommentButtonTooltip = (): string | undefined => {
    if (!hrComment || hrComment.trim().length === 0) {
      return t('companySettingsView.appraisalTab.table.feedback.modal.hrComment.btnTooltip.emptyComment')
    } else if (!!hrComment && hrComment.trim().length < 2) {
      return t('companySettingsView.appraisalTab.table.feedback.modal.hrComment.btnTooltip.commentTooShort')
    } else return undefined
  }

  return (
    <PerModal
      title={{
        heading: hrFeedbackIsOpen
          ? t('companySettingsView.appraisalTab.table.feedback.modal.heading.review')
          : t('companySettingsView.appraisalTab.table.feedback.modal.heading.resolved'),
        subheading: t('companySettingsView.appraisalTab.table.feedback.modal.heading.subheading', {
          employee: `${modalData.replyer.firstName} ${modalData.replyer.lastName}`,
          date: formatDateShort(modalData.date),
          reviewer: '',
        }),
      }}
      size="large"
      visible={open}
      onCancel={() => {
        closeModal()
      }}
      footer={{
        cancelBtn: {
          onClick: () => {
            closeModal()
          },
        },
        customBtns: [
          {
            visible: modalData.conclusionPdf ? true : false,
            text: t('companySettingsView.appraisalTab.table.feedback.modal.btnConclusion'),
            color: 'primary',
            icon: 'view_fill',
            asLink: modalData.conclusionPdf
              ? getConclusionHrefByMeeting({
                  id: modalData.id,
                  conclusionPdfFileName: modalData.conclusionPdf.fileName,
                  conclusionPdfDownloadToken: modalData.conclusionPdf.downloadToken,
                  conclusionPdfStatus: modalData.conclusionPdf.status,
                })
              : undefined,
            disabled:
              !modalData.conclusionPdf ||
              !modalData.conclusionPdf.status ||
              modalData.conclusionPdf.status !== MeetingPdfStatus.FinishedSuccess,
          },
        ],
        confirmBtn: {
          visible: hrFeedbackIsOpen,
          disabled:
            hrResult === MeetingHrFeedbackResult.NoResult ||
            !hrComment ||
            hrComment.trim().length < 2 ||
            saveCommentInProgress ||
            resolveFeedbackInProgress,
          isProcessing: { active: isSubmitting },
          tooltip: createConfirmButtonTooltip(),
          onClick: () => {
            if (!!hrComment && hrComment.trim().length >= 2 && hrResult !== MeetingHrFeedbackResult.NoResult) {
              toggleSubmitting(true)
              onSubmit({
                employeeId: modalData.replyer.id,
                hrManageMeetingInput: {
                  meetingId: modalData.id,
                  hrDisputeComment: hrComment,
                  hrResult: hrResult,
                },
              })
              closeModal()
              toggleSubmitting(false)
            }
          },
        },
      }}
    >
      <PerCardInfo heading={feedbackHeading(meetingStatus, modalData.replyer.firstName)} icon={{ color: feedbackColor(meetingStatus) }}>
        <PerText appearance="quote">{modalData.hrFeedback}</PerText>
      </PerCardInfo>

      <>
        <Divider />

        <PerText appearance="subheading">{t('companySettingsView.appraisalTab.table.feedback.modal.hrComment.heading')}</PerText>
        {hrFeedbackIsOpen ? (
          <>
            <TextArea
              name="hrDisputeComment"
              placeholder={t('companySettingsView.appraisalTab.table.feedback.modal.hrComment.inputPlaceholder')}
              autoSize={{ minRows: 3, maxRows: 6 }}
              onChange={(val) => {
                setHrComment(val.target.value)
              }}
              value={hrComment}
            />

            <PerFlexRow justify="end">
              <PerButton
                text={t('companySettingsView.appraisalTab.table.feedback.modal.hrComment.btnSave')}
                disabled={saveCommentInProgress || resolveFeedbackInProgress || !hrComment || hrComment.trim().length < 2}
                tooltip={createSaveCommentButtonTooltip()}
                color="success"
                variant="pastel"
                size="small"
                mt="s"
                type="button"
                onClick={() => {
                  setHrComment(hrComment!.trim())
                  saveComment({
                    employeeId: modalData.replyer.id,
                    hrManageMeetingInput: {
                      meetingId: modalData.id,
                      hrDisputeComment: hrComment!.trim(),
                      hrResult: MeetingHrFeedbackResult.NoResult,
                    },
                  })
                }}
              />
            </PerFlexRow>
          </>
        ) : (
          <PerText appearance="quote">{modalData.hrDisputeComment}</PerText>
        )}
      </>

      <>
        <Divider />

        <PerText appearance="subheading">{t('companySettingsView.appraisalTab.table.feedback.modal.result.heading')}</PerText>
        {hrFeedbackIsOpen ? (
          <>
            <PerText>
              {`${t('companySettingsView.appraisalTab.table.feedback.modal.result.edit.description', {
                employee: modalData.replyer.firstName,
              })}:`}
            </PerText>
            <PerFlexRow direction="column" mt="s">
              <RadioGroup
                name="hrResult"
                onChange={(event) => {
                  setHrResult(event.target.value)
                }}
                value={hrResult}
              >
                <Radio name="hrResult" value={MeetingHrFeedbackResult.KeepingChanges}>
                  {t('companySettingsView.appraisalTab.table.feedback.modal.result.edit.option.keeping')}
                </Radio>
                <PerSpacer mt={'xxs'} />
                <Radio name="hrResult" value={MeetingHrFeedbackResult.AppraisalUpdate}>
                  {t('companySettingsView.appraisalTab.table.feedback.modal.result.edit.option.appraisalUpdateMeeting')}
                </Radio>
              </RadioGroup>
            </PerFlexRow>
          </>
        ) : (
          <PerText display="inline">
            {t('companySettingsView.appraisalTab.table.feedback.modal.result.view.description', {
              employee: modalData.replyer.firstName,
            })}
            <PerText color={feedbackResultColor(modalData.hrResult)} display="inline">
              {feedbackResultText(modalData.hrResult)}
            </PerText>
            .
          </PerText>
        )}
        {modalError && (
          <PerText color="error" fontSize="m" mt="m">
            {modalError}
          </PerText>
        )}
      </>
    </PerModal>
  )
}
